/**
 * The status chip for a campaign.
 *
 * Every member of `CampaignStatus` gets a chip, from the one table in
 * `campaign-display.ts`, with no default arm. A default is how a status added by
 * a later slice renders as a blank rather than as something a reader can act on.
 *
 * This header used to say `paused_insufficient_credits` could not be reached
 * "in this version". That was true in slice 5 and stopped being true in slice 6,
 * when `campaign-send.runner.ts` began writing it; the sentence sat here
 * unchallenged for four slices and the same claim was on the screen. Left
 * standing it argues the next reader into treating this chip as decoration for
 * a state that only happens by accident — it is the state a tenant hits when
 * their credit runs out mid-send.
 *
 * **Slice 66 gave it the dot** — "● Sending" — which is the Blueprint's status
 * pill. The dot is `aria-hidden` and painted in the badge's own colour, so it
 * adds a shape the eye can find down a column without adding a second carrier
 * of meaning: the word is still the status. The tones are unchanged, and
 * deliberately so — they come from `CAMPAIGN_STATUS_DISPLAY`, and a campaign's
 * status is a real status, so status colour is right here.
 */
import type { CampaignStatus } from '@wend/shared';
import { Badge } from '@wend/ui';

import { CAMPAIGN_STATUS_DISPLAY } from '../lib/campaign-display';

export interface CampaignStatusBadgeProps {
  status: CampaignStatus;
}

export function CampaignStatusBadge({ status }: CampaignStatusBadgeProps) {
  const display = CAMPAIGN_STATUS_DISPLAY[status];
  return (
    <Badge tone={display.tone} dot>
      {display.label}
    </Badge>
  );
}
